import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import {
  signIn,
  confirmSignIn,
  signOut,
  fetchAuthSession,
  getCurrentUser,
  // fetchMFAPreference,
  signUp,
  confirmSignUp,
  autoSignIn,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
  // type ConfirmSignUpInput,
} from 'aws-amplify/auth';
import { message } from 'antd';
import { setItemWithExpiry, removeItemWithExpiry, getItemWithExpiry } from '@/util/util';
import { store } from '@/store';
import { setUser } from '@/store/features/userinfo/userinfoSlice';
import { resetAllUserData } from '@/store/rootReducer';
import { setSentryUser, clearSentryUser } from '@/sentry';
import { completeAccountSetup, completeInvestorSignup, fetchUserDetails } from '@/api/custom';
import {
  clearPendingLocalInvestorSignup,
  getPendingLocalInvestorSignupUserId,
} from '@/util/localInvestorSignup';
import {
  buildInvestorSignupSupportSignalRequest,
  INVESTOR_SIGNUP_COMPLETION_ERROR_KIND,
  INVESTOR_SIGNUP_COMPLETION_FAILED_KIND,
} from '@/util/investorSignupSupportSignal';
import {
  buildScreenshotGalleryConfirmForgotPasswordError,
  buildScreenshotGalleryConfirmationError,
  buildScreenshotGalleryForgotPasswordError,
  buildScreenshotGallerySignUpError,
  getScreenshotGalleryAuthScenario,
  isConfirmationScenario,
} from './authScreenshotGalleryScenarios';
import { AUTH_COPY } from './authCopy';

/**
 * Helper function to extract error code from Amplify v6 errors
 * Amplify v6 surfaces errors via both error.code (Cognito service errors)
 * and error.name (Amplify-level exceptions)
 */
const getErrorCode = (error: any): string => {
  return error?.code || error?.name || '';
};

const isAlreadyConfirmedSignUpError = (error: any): boolean => {
  const errorMessage = error?.message || '';
  const errorCode = getErrorCode(error);

  return (
    errorCode === 'AlreadyConfirmedException' ||
    errorMessage.includes('Current status is CONFIRMED')
  );
};

const isPostConfirmationInvocationError = (error: any): boolean => {
  const errorMessage = error?.message || '';
  return errorMessage.toLowerCase().includes('postconfirmation invocation failed');
};

const getErrorMessage = (error: any): string => {
  return error?.message || '';
};

const getFriendlySignUpConfirmationErrorMessage = (error: any): string => {
  const errorCode = getErrorCode(error);
  const rawMessage = getErrorMessage(error);

  if (errorCode === 'CodeMismatchException') {
    return "That confirmation code didn't match. Check the 6-digit code from your email and try again.";
  }
  if (errorCode === 'ExpiredCodeException') {
    return 'That confirmation code has expired. Please request a new code and try again.';
  }
  if (errorCode === 'LimitExceededException') {
    return 'Too many attempts were detected. Please wait a few minutes before trying again.';
  }
  if (isAlreadyConfirmedSignUpError(error)) {
    return AUTH_COPY.confirmation.alreadyConfirmed;
  }

  if (
    rawMessage.includes('No investor found for token') ||
    rawMessage.includes('Token not found or invalid')
  ) {
    return 'This signup link is no longer active. Please ask your advisor for a new signup link.';
  }
  if (rawMessage.includes('Malformed investor signup token')) {
    return "This signup link isn't valid. Please ask your advisor for a new signup link.";
  }
  if (
    rawMessage.includes('Investor already has a Wavvest account') ||
    rawMessage.includes('Investor already signed up for a Wavvest account')
  ) {
    return 'This account has already been claimed. Please sign in with your existing account or reset your password.';
  }
  if (rawMessage.includes('already in use')) {
    return 'That email address is already tied to a Wavvest account. Please sign in with that account or reset your password.';
  }
  if (
    rawMessage.includes('Missing Cognito user id for investor signup completion') ||
    rawMessage.includes('Missing Cognito user id for local investor signup completion') ||
    rawMessage.includes('Unable to sign in after investor signup confirmation') ||
    rawMessage.includes('Internal server error while completing investor signup')
  ) {
    return "We couldn't finish setting up your account. Try signing in now. If that doesn't work, contact your advisor or support for help.";
  }

  return rawMessage || "We couldn't verify your email. Please try again.";
};

const genericInvestorSignupCompletionErrorMessage =
  "We couldn't finish setting up your account. Try signing in now. If that doesn't work, contact your advisor or support for help.";

const getFriendlyInvestorSignupCompletionErrorMessage = (error: any): string => {
  const friendlyMessage = getFriendlySignUpConfirmationErrorMessage(error);
  const rawMessage = getErrorMessage(error);

  if (!rawMessage || !friendlyMessage || friendlyMessage === rawMessage) {
    return genericInvestorSignupCompletionErrorMessage;
  }

  return friendlyMessage;
};

const getFriendlyResendSignUpCodeErrorMessage = (
  error: any,
): {
  kind: string;
  message: string;
} => {
  const errorCode = getErrorCode(error);

  if (errorCode === 'LimitExceededException') {
    return {
      kind: 'LIMIT_EXCEEDED',
      message:
        'Too many resend attempts were detected. Please wait a few minutes before trying again, or check your email for a previous code that may still be valid.',
    };
  }
  if (errorCode === 'InvalidParameterException') {
    return {
      kind: 'INVALID_PARAMETER',
      message:
        "We couldn't resend the verification code for this email. Use the code from your signup email, or ask your advisor for help.",
    };
  }
  if (errorCode === 'UserNotFoundException') {
    return {
      kind: 'USER_NOT_FOUND',
      message:
        "We couldn't find a signup in progress for this email address. Check the email address, use the code from your signup email, or ask your advisor for a new invitation link.",
    };
  }

  return {
    kind: 'UNKNOWN_ERROR',
    message: "We couldn't resend the confirmation code right now. Please try again later today.",
  };
};

const getFriendlySignUpErrorMessage = (error: any): string => {
  const errorCode = getErrorCode(error);
  const rawMessage = getErrorMessage(error);

  if (errorCode === 'InvalidPasswordException') {
    return AUTH_COPY.signup.passwordRequirements;
  }
  if (errorCode === 'InvalidParameterException') {
    return AUTH_COPY.signup.invalidDetails;
  }
  if (errorCode === 'LimitExceededException' || errorCode === 'TooManyRequestsException') {
    return AUTH_COPY.signup.tooManyAttempts;
  }
  if (errorCode === 'CodeDeliveryFailureException') {
    return AUTH_COPY.signup.codeDeliveryFailed;
  }
  if (
    errorCode === 'NetworkError' ||
    rawMessage.includes('Network Error') ||
    rawMessage.includes('Failed to fetch') ||
    rawMessage.includes('Load failed') ||
    rawMessage.includes('Network request failed')
  ) {
    return AUTH_COPY.signup.serviceUnavailable;
  }

  return AUTH_COPY.signup.failed;
};

const signupEmailUnavailableMessage = AUTH_COPY.signup.accountExists;

const pendingSignupVerificationCodeSentMessage =
  'We sent a verification code to your email. Enter it to verify your email before resetting your password.';

const pendingSignupVerificationCodeRecentlySentMessage =
  'A verification code was already sent recently. Enter that code to verify your email before resetting your password.';

const INVESTOR_SIGNUP_TOKEN_PREFIX = 'custinvite-';

const isInvestorSignupToken = (inviteToken: string): boolean =>
  inviteToken.startsWith(INVESTOR_SIGNUP_TOKEN_PREFIX);

const buildInvestorSignupCompletionFailureResult = (
  inviteToken: string,
  error: unknown,
): ConfirmSignUpResult => {
  const supportSignalRequest = buildInvestorSignupSupportSignalRequest({
    token: inviteToken,
    error,
  });

  return {
    kind: INVESTOR_SIGNUP_COMPLETION_FAILED_KIND,
    message: 'Investor signup completion failed',
    request_id: supportSignalRequest.request_id,
    completion_attempt_id: supportSignalRequest.completion_attempt_id,
    normalized_error_fingerprint: supportSignalRequest.normalized_error_fingerprint,
    sentry_event_id: supportSignalRequest.sentry_event_id,
    sentry_issue_id: supportSignalRequest.sentry_issue_id,
  };
};

const buildInvestorSignupCompletionErrorResult = (error: unknown): ConfirmSignUpResult => ({
  kind: INVESTOR_SIGNUP_COMPLETION_ERROR_KIND,
  message: getFriendlyInvestorSignupCompletionErrorMessage(error),
});

interface User {
  userId: string;
  username: string;
  [key: string]: any;
}

type SignUpParameters = {
  email: string;
  password: string;
  fullname: string;
  preferredName: string;
};

type ConfirmSignUpParameters = {
  username: string;
  confirmationCode: string;
  inviteToken?: string;
  userId?: string;
  password?: string;
  completeAutoSignIn?: boolean;
};

interface SignUpResult {
  kind: string;
  isSignUpComplete?: boolean;
  userId?: string;
  nextStep?: any;
  message?: string;
  field?: 'email';
}

interface ConfirmSignUpResult {
  kind: string;
  isSignUpComplete?: boolean;
  nextStep?: any;
  message?: string;
  request_id?: string;
  completion_attempt_id?: string;
  normalized_error_fingerprint?: string;
  sentry_event_id?: string;
  sentry_issue_id?: string;
}

interface ResendSignUpCodeResult {
  kind: string;
  message?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  recoverAccountSetup: (inviteToken: string) => Promise<LoginResult>;
  resumeCurrentSession: () => Promise<LoginResult>;
  logout: () => Promise<void>;
  handleSignUp: (params: SignUpParameters) => Promise<SignUpResult>;
  signUpConfirmation: (params: ConfirmSignUpParameters) => Promise<ConfirmSignUpResult>;
  confirmMfaSignIn: (challengeResponse: string, email: string) => Promise<LoginResult>;
  resendSignUpCodeHandler: (username: string) => Promise<ResendSignUpCodeResult>;
  completeConfirmedSignUp: () => Promise<LoginResult>;
  forgotPassword: (email: string) => Promise<ForgotPasswordResult>;
  confirmForgotPassword: (
    params: ConfirmForgotPasswordParameters,
  ) => Promise<ConfirmForgotPasswordResult>;
  clearSession: () => Promise<void>;
  forceLogout: () => Promise<void>;
  detectUserSwitch: () => Promise<boolean>;
  isAuthenticated: boolean;
  refreshUserData: () => Promise<void>;
}

// Sign-in steps that are answered with a one-time code via confirmSignIn().
const MFA_CODE_SIGN_IN_STEPS: string[] = [
  'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
  'CONFIRM_SIGN_IN_WITH_SMS_CODE',
  'CONFIRM_SIGN_IN_WITH_EMAIL_CODE',
];

// The user has more than one factor enrolled and Cognito wants to know which
// to use. Answered with confirmSignIn({ challengeResponse: 'TOTP' | 'SMS' |
// 'EMAIL' }), after which Cognito issues the matching code step.
const MFA_SELECTION_SIGN_IN_STEP = 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION';

// Cognito wants the user to *enrol* a factor before signing in, which only
// happens on pools with MFA set to required. Completing these means building
// enrolment into the login page (QR code, secret, verification) — that belongs
// with the Profile & Security work, so for now we identify them and say so
// plainly instead of reporting a failed sign-in.
const MFA_SETUP_SIGN_IN_STEPS: string[] = [
  'CONTINUE_SIGN_IN_WITH_TOTP_SETUP',
  'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP',
  'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION',
];

/**
 * Map an unfinished sign-in to a LoginResult.
 *
 * Shared by login() and confirmMfaSignIn() so a challenge is classified the
 * same way wherever Cognito raises it — answering one challenge frequently
 * yields another (factor selection -> code), and the two must not disagree.
 * Returns null when the step isn't one we recognise.
 */
const mfaLoginResultForSignInStep = (
  nextStep: { signInStep: string; allowedMFATypes?: string[] },
  email: string,
): LoginResult | null => {
  if (MFA_CODE_SIGN_IN_STEPS.includes(nextStep.signInStep)) {
    return {
      kind: 'MFA_CODE_REQUIRED',
      message: AUTH_COPY.login.mfaCodeRequired,
      username: email,
      mfaSignInStep: nextStep.signInStep,
    };
  }

  if (nextStep.signInStep === MFA_SELECTION_SIGN_IN_STEP) {
    return {
      kind: 'MFA_SELECTION_REQUIRED',
      message: AUTH_COPY.login.mfaSelectionRequired,
      username: email,
      mfaSignInStep: nextStep.signInStep,
      allowedMfaTypes: nextStep.allowedMFATypes ?? [],
    };
  }

  if (MFA_SETUP_SIGN_IN_STEPS.includes(nextStep.signInStep)) {
    return {
      kind: 'MFA_SETUP_REQUIRED',
      message: AUTH_COPY.login.mfaSetupUnsupported,
      username: email,
      mfaSignInStep: nextStep.signInStep,
    };
  }

  return null;
};

interface LoginResult {
  kind: string;
  /** Which Cognito challenge is outstanding, for the MFA_* kinds. */
  mfaSignInStep?: string;
  /** Factors the user may pick from, when kind is MFA_SELECTION_REQUIRED. */
  allowedMfaTypes?: string[];
  access_token?: string;
  user?: User | null;
  message?: string;
  username?: string;
  userId?: string;
  supportFingerprint?: string;
}

type ConfirmForgotPasswordParameters = {
  email: string;
  confirmationCode: string;
  newPassword: string;
};

interface ForgotPasswordResult {
  kind: string;
  message?: string;
  resendCodeWaitSeconds?: number;
}

interface ConfirmForgotPasswordResult {
  kind: string;
  message?: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

// session check interval, default 30 seconds
const SESSION_CHECK_INTERVAL = 30000;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // use ref to avoid useEffect dependency problem
  const sessionCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const isCheckingSession = useRef<boolean>(false);

  const hydrateAuthenticatedUser = useCallback(
    async (
      authUser: User,
    ): Promise<
      | { kind: 'success'; user: User }
      | {
          kind:
            | 'ACCOUNT_SETUP_INCOMPLETE'
            | 'PROFILE_SUPPORT_REQUIRED'
            | 'PROFILE_UNAVAILABLE'
            | 'AUTH_REQUIRED';
          message: string;
          supportFingerprint?: string;
        }
    > => {
      const userDetailsResult = await fetchUserDetails(authUser.userId);

      if (userDetailsResult.kind === 'success') {
        const enrichedUser = {
          ...authUser,
          ...userDetailsResult.data,
        };
        return { kind: 'success', user: enrichedUser };
      }

      if (userDetailsResult.kind === 'NO_INVESTOR_RECORD') {
        return {
          kind: 'ACCOUNT_SETUP_INCOMPLETE',
          message: userDetailsResult.message,
          supportFingerprint: userDetailsResult.supportFingerprint,
        };
      }

      if (userDetailsResult.kind === 'PROFILE_SUPPORT_REQUIRED') {
        return {
          kind: 'PROFILE_SUPPORT_REQUIRED',
          message: userDetailsResult.message,
          supportFingerprint: userDetailsResult.supportFingerprint,
        };
      }

      if (userDetailsResult.kind === 'AUTH_REQUIRED') {
        return {
          kind: 'AUTH_REQUIRED',
          message: userDetailsResult.message,
        };
      }

      return {
        kind: 'PROFILE_UNAVAILABLE',
        message: userDetailsResult.message,
      };
    },
    [],
  );

  // function to detect user switch
  const detectUserSwitch = useCallback(async (): Promise<boolean> => {
    try {
      const localUser = getItemWithExpiry('user');

      if (!localUser) {
        return false;
      }

      const localUserData = JSON.parse(localUser);

      try {
        const currentCognitoUser = await getCurrentUser();

        if (localUserData.userId !== currentCognitoUser.userId) {
          console.warn('User switch detected:', {
            localUserId: localUserData.userId,
            cognitoUserId: currentCognitoUser.userId,
          });
          return true;
        }
      } catch (cognitoError) {
        // if cannot get cognito user, but local user exists, it is also a conflict
        console.warn('Cognito user not found but local user exists');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error detecting user switch:', error);
      return false;
    }
  }, []);

  // force logout function
  const forceLogout = useCallback(async (): Promise<void> => {
    try {
      // console.log("Performing force logout...");

      // stop session check
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
        sessionCheckInterval.current = null;
      }

      // 1. try to sign out from AWS Cognito
      try {
        await signOut({ global: true });
      } catch (signOutError) {
        console.warn('Cognito signOut failed:', signOutError);
      }

      // 2. clear all local storage
      removeItemWithExpiry('access_token');
      removeItemWithExpiry('user');

      // 3. clear all Cognito related localStorage keys
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('CognitoIdentityServiceProvider') ||
          key.startsWith('aws-amplify-') ||
          key.includes('cognito')
        ) {
          localStorage.removeItem(key);
        }
      });

      // 4. clear sessionStorage
      Object.keys(sessionStorage).forEach((key) => {
        if (
          key.startsWith('CognitoIdentityServiceProvider') ||
          key.startsWith('aws-amplify-') ||
          key.includes('cognito')
        ) {
          sessionStorage.removeItem(key);
        }
      });

      // 5. reset application state
      setUserState(null);
      store.dispatch(resetAllUserData());

      // console.log("Force logout completed successfully");
    } catch (error) {
      console.error('Force logout failed:', error);
      throw error;
    }
  }, []);

  // session validation function
  const validateSession = useCallback(async (): Promise<boolean> => {
    if (isCheckingSession.current) {
      return true; // if is checking, avoid duplicate execution
    }

    isCheckingSession.current = true;

    try {
      // check user switch
      const hasUserSwitch = await detectUserSwitch();
      if (hasUserSwitch) {
        console.warn('Session validation failed: user switch detected');
        await forceLogout();
        return false;
      }

      // check token validity
      const localUser = getItemWithExpiry('user');
      const accessToken = getItemWithExpiry('access_token');

      if (localUser && accessToken) {
        try {
          const session = await fetchAuthSession();
          if (!session?.tokens?.idToken) {
            console.warn('Session validation failed: invalid tokens');
            await forceLogout();
            return false;
          }
        } catch (sessionError) {
          console.warn('Session validation failed: session error', sessionError);
          await forceLogout();
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    } finally {
      isCheckingSession.current = false;
    }
  }, [detectUserSwitch, forceLogout]);

  // start periodic session check
  const startSessionMonitoring = useCallback(() => {
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
    }

    sessionCheckInterval.current = setInterval(async () => {
      if (user) {
        const isValid = await validateSession();
        if (!isValid) {
          message.warning('detected session exception, auto logout');
          // can add page redirect logic here
          window.location.href = '/';
        }
      }
    }, SESSION_CHECK_INTERVAL);
  }, [user, validateSession]);

  // stop session monitoring
  const stopSessionMonitoring = useCallback(() => {
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
      sessionCheckInterval.current = null;
    }
  }, []);

  const storeAuthenticatedUserSession = useCallback(
    (authenticatedUser: User, token: string) => {
      setItemWithExpiry('access_token', token, 8 * 60 * 60 * 1000);
      setItemWithExpiry('user', JSON.stringify(authenticatedUser), 8 * 60 * 60 * 1000);

      setUserState(authenticatedUser);
      store.dispatch(setUser(authenticatedUser));

      setSentryUser({
        id: authenticatedUser.userId,
        username: authenticatedUser.username,
      });

      startSessionMonitoring();
    },
    [startSessionMonitoring],
  );

  const checkUser = async () => {
    try {
      const userStr = getItemWithExpiry('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const cachedToken = getItemWithExpiry('access_token');
        if (!cachedToken) {
          console.warn('Cached user found without cached auth token, forcing logout');
          await forceLogout();
          return;
        }

        let currentCognitoUser: Awaited<ReturnType<typeof getCurrentUser>>;
        try {
          currentCognitoUser = await getCurrentUser();
        } catch {
          console.warn('Cached user found without current Cognito user, forcing logout');
          await forceLogout();
          return;
        }

        if (userData.userId !== currentCognitoUser.userId) {
          console.warn('User switch detected during checkUser, forcing logout', {
            localUserId: userData.userId,
            cognitoUserId: currentCognitoUser.userId,
          });
          await forceLogout();
          return;
        }

        let currentToken: string | undefined;
        try {
          const session = await fetchAuthSession();
          currentToken = session?.tokens?.idToken?.toString();
        } catch {
          console.warn('Cached user found without current Cognito session, forcing logout');
          await forceLogout();
          return;
        }

        if (!currentToken) {
          console.warn('Cached user found without current Cognito ID token, forcing logout');
          await forceLogout();
          return;
        }

        storeAuthenticatedUserSession(userData, currentToken);
      } else {
        // try to get current cognito user
        try {
          const cognitoUser = await getCurrentUser();
          // if get cognito user, but no user info in local storage, it may be session conflict
          // check if this user is the one we expect
          const session = await fetchAuthSession();
          if (session?.tokens?.idToken) {
            // if has valid token, try to get user details
            const hydratedUser = await hydrateAuthenticatedUser(cognitoUser);
            if (hydratedUser.kind === 'success') {
              setItemWithExpiry('user', JSON.stringify(hydratedUser.user), 8 * 60 * 60 * 1000);
              setItemWithExpiry(
                'access_token',
                session.tokens.idToken.toString(),
                8 * 60 * 60 * 1000,
              );
              setUserState(hydratedUser.user);
              store.dispatch(setUser(hydratedUser.user));

              // start session monitoring
              startSessionMonitoring();
            } else {
              setError(hydratedUser.message);
              if (hydratedUser.kind === 'AUTH_REQUIRED') {
                console.warn(
                  'Found Cognito session but profile hydration requires a new sign-in, clearing session',
                  hydratedUser,
                );
                await forceLogout();
              } else if (hydratedUser.kind === 'PROFILE_UNAVAILABLE') {
                console.warn(
                  'Found Cognito session but unable to hydrate user profile',
                  hydratedUser,
                );
              } else {
                console.warn(
                  'Found Cognito session with a durable account profile issue',
                  hydratedUser,
                );
                setUserState(null);
                stopSessionMonitoring();
              }
            }
          } else {
            // if no valid token, clear session
            await forceLogout();
          }
        } catch (cognitoError) {
          setUserState(null);
          stopSessionMonitoring();
        }
      }
    } catch (err: unknown) {
      console.error('Error in checkUser:', err);
      setUserState(null);
      stopSessionMonitoring();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUser();

    // listen to localStorage change
    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key?.includes('user') ||
        e.key?.includes('access_token') ||
        e.key?.startsWith('CognitoIdentityServiceProvider')
      ) {
        // console.log("Storage change detected:", e.key);
        // delay execution, avoid frequent triggering
        setTimeout(() => {
          checkUser();
        }, 100);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // listen to page visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        // when page becomes visible, validate session
        validateSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopSessionMonitoring();
    };
  }, []);

  // Everything that has to happen once Cognito reports a signed-in session.
  // Shared by the password-only path and the MFA path so the two can't drift.
  const completeAuthenticatedSignIn = async (email: string): Promise<LoginResult> => {
    const userData = await getCurrentUser();
    const hydratedUser = await hydrateAuthenticatedUser(userData);
    if (hydratedUser.kind !== 'success') {
      setError(hydratedUser.message);
      if (hydratedUser.kind === 'PROFILE_UNAVAILABLE') {
        console.warn('Profile hydration failed after sign-in', hydratedUser);
      } else if (hydratedUser.kind === 'AUTH_REQUIRED') {
        await forceLogout();
      }
      const loginResult: LoginResult = {
        kind: hydratedUser.kind,
        message: hydratedUser.message,
        username: email,
        userId: userData.userId,
      };
      if (hydratedUser.supportFingerprint) {
        loginResult.supportFingerprint = hydratedUser.supportFingerprint;
      }
      return loginResult;
    }

    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString() || '';
    storeAuthenticatedUserSession(hydratedUser.user, token);

    return {
      kind: 'success',
      access_token: token,
      user: hydratedUser.user,
    };
  };

  /**
   * Answer the MFA challenge left open by login().
   *
   * Kept separate from login() because Cognito holds the half-finished sign-in
   * server-side — the password must not be re-sent, only the answer. The
   * answer is a one-time code, or a factor name ('TOTP' | 'SMS' | 'EMAIL')
   * when Cognito asked the user to choose.
   */
  const confirmMfaSignIn = async (
    challengeResponse: string,
    email: string,
  ): Promise<LoginResult> => {
    setLoading(true);
    setError(null);
    try {
      const response = await confirmSignIn({ challengeResponse: challengeResponse.trim() });

      if (response.isSignedIn) {
        // The code has been accepted and consumed by this point. If finishing
        // the session throws, the challenge is gone, so a retryable result
        // would strand the user on a code form that can never succeed. Clear
        // the half-made session and send them back to the password step.
        try {
          return await completeAuthenticatedSignIn(email);
        } catch (completionError) {
          console.error('Failed to complete sign-in after MFA', completionError);
          await forceLogout();
          setError(AUTH_COPY.login.sessionExpired);
          return { kind: 'AUTH_REQUIRED', message: AUTH_COPY.login.sessionExpired };
        }
      }

      // Answering one challenge often raises another — picking a factor yields
      // its code step — so classify the new one the same way login() does.
      const mfaResult = mfaLoginResultForSignInStep(response.nextStep, email);
      if (mfaResult) {
        if (mfaResult.kind === 'MFA_SETUP_REQUIRED') setError(mfaResult.message ?? null);
        return mfaResult;
      }

      console.warn('Unhandled sign-in step after MFA', response.nextStep.signInStep);
      setError(AUTH_COPY.login.signInFailed);
      return { kind: 'error', message: AUTH_COPY.login.signInFailed };
    } catch (error: unknown) {
      const err = error as { name?: string; message?: string };
      // Reaching here means confirmSignIn itself threw, so the challenge is
      // still open (completion failures are handled above). A wrong or expired
      // code is recoverable — report it inline and let the user try again.
      if (err.name === 'CodeMismatchException' || err.name === 'EnableSoftwareTokenMFAException') {
        setError(AUTH_COPY.login.mfaCodeInvalid);
        return { kind: 'MFA_CODE_INVALID', message: AUTH_COPY.login.mfaCodeInvalid };
      }
      // The challenge itself expired; the user has to start from the password.
      if (err.name === 'NotAuthorizedException' || err.name === 'ExpiredCodeException') {
        setError(AUTH_COPY.login.mfaSessionExpired);
        return { kind: 'MFA_SESSION_EXPIRED', message: AUTH_COPY.login.mfaSessionExpired };
      }
      console.error('Failed to confirm MFA sign-in', error);
      setError(AUTH_COPY.login.signInFailed);
      return { kind: 'error', message: AUTH_COPY.login.signInFailed };
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const screenshotGalleryScenario = getScreenshotGalleryAuthScenario();
      switch (screenshotGalleryScenario) {
        case 'login-wrong-credentials':
          setError(AUTH_COPY.login.invalidCredentials);
          return {
            kind: 'WRONG_CREDENTIAL',
            message: AUTH_COPY.login.invalidCredentials,
          };

        case 'login-user-not-found':
          setError(AUTH_COPY.login.invalidCredentials);
          return {
            kind: 'USER_NOT_FOUND',
            message: AUTH_COPY.login.invalidCredentials,
          };

        case 'login-unconfirmed-account':
          return {
            kind: 'USER_NOT_CONFIRMED',
            message: AUTH_COPY.login.unconfirmedAccount,
            username: email,
          };

        case 'login-legacy-password-reset':
          return {
            kind: 'LEGACY_PASSWORD_RESET_REQUIRED',
            message: AUTH_COPY.login.legacyPasswordResetRequired,
            username: email,
          };

        case 'login-account-setup-incomplete':
          setError(AUTH_COPY.accountSetup.supportBody);
          return {
            kind: 'ACCOUNT_SETUP_INCOMPLETE',
            message: AUTH_COPY.accountSetup.supportBody,
            username: email,
            userId: 'screenshot-gallery-user',
          };

        case 'login-profile-support-required':
          setError(AUTH_COPY.accountSetup.supportBody);
          return {
            kind: 'PROFILE_SUPPORT_REQUIRED',
            message: AUTH_COPY.accountSetup.supportBody,
            username: email,
            userId: 'screenshot-gallery-user',
            supportFingerprint: 'screenshot_gallery:profile_support_required',
          };

        case 'login-profile-unavailable':
          setError(AUTH_COPY.accountSetup.profileLoadFailed);
          return {
            kind: 'PROFILE_UNAVAILABLE',
            message: AUTH_COPY.accountSetup.profileLoadFailed,
            username: email,
            userId: 'screenshot-gallery-user',
          };

        case 'login-auth-required':
          setError(AUTH_COPY.login.sessionExpired);
          return {
            kind: 'AUTH_REQUIRED',
            message: AUTH_COPY.login.sessionExpired,
            username: email,
            userId: 'screenshot-gallery-user',
          };

        default:
          break;
      }

      // before login, perform comprehensive check and cleanup
      // console.log("Starting login process for:", email);

      // 1. check user switch
      const hasUserSwitch = await detectUserSwitch();
      if (hasUserSwitch) {
        // console.log("User switch detected, performing force logout");
        await forceLogout();
      }

      // 2. check existing Cognito session
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          // console.log("Found existing user session, signing out first");
          await forceLogout();
          // wait for a short time to ensure cleanup is complete
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (noUserError) {
        console.log('No existing user session found, proceeding with login');
      }

      const response = await signIn({ username: email, password });

      if (response.isSignedIn) {
        return await completeAuthenticatedSignIn(email);
      } else if (response.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return {
          kind: 'LEGACY_PASSWORD_RESET_REQUIRED',
          message: AUTH_COPY.login.legacyPasswordResetRequired,
          username: email,
        };
      } else if (response.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        console.log('User account not confirmed, showing inline verification');
        return {
          kind: 'USER_NOT_CONFIRMED',
          message: AUTH_COPY.login.unconfirmedAccount,
          username: email,
        };
      }

      // Cognito accepted the password and raised a second-factor challenge.
      // The sign-in stays mid-flight until confirmMfaSignIn() answers it.
      const mfaResult = mfaLoginResultForSignInStep(response.nextStep, email);
      if (mfaResult) {
        if (mfaResult.kind === 'MFA_SETUP_REQUIRED') setError(mfaResult.message ?? null);
        return mfaResult;
      }

      console.warn('Unhandled sign-in step', response.nextStep.signInStep);
      setError(AUTH_COPY.login.signInFailed);
      return { kind: 'error', message: AUTH_COPY.login.signInFailed };
    } catch (error: unknown) {
      let errorMessage: string = AUTH_COPY.login.signInFailed;
      let errorKind: string = 'UNKNOWN_ERROR';

      const err = error as { code?: string; message?: string };

      if (err.code === 'UserNotFoundException') {
        errorKind = 'USER_NOT_FOUND';
        errorMessage = AUTH_COPY.login.invalidCredentials;
      } else if (err.code === 'NotAuthorizedException') {
        errorKind = 'WRONG_CREDENTIAL';
        errorMessage = AUTH_COPY.login.invalidCredentials;
      } else if (err.message?.includes('already a signed in user')) {
        errorKind = 'USER_ALREADY_SIGNED_IN';
        errorMessage = AUTH_COPY.login.signInFailed;
        try {
          await forceLogout();
        } catch (signOutError) {
          console.error('Error during force logout:', signOutError);
        }
      }

      setError(errorMessage);
      return {
        kind: errorKind,
        message: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  const recoverAccountSetup = async (inviteToken: string): Promise<LoginResult> => {
    setLoading(true);
    setError(null);

    try {
      const authUser = await getCurrentUser();
      const setupResult = await completeAccountSetup(authUser.userId, inviteToken);

      if (setupResult.kind !== 'success') {
        setError(setupResult.message);
        return {
          kind: 'ACCOUNT_SETUP_INCOMPLETE',
          message: setupResult.message,
        };
      }

      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString() || '';
      const enrichedUser = {
        ...authUser,
        ...setupResult.data,
      };

      storeAuthenticatedUserSession(enrichedUser, token);

      return {
        kind: 'success',
        access_token: token,
        user: enrichedUser,
      };
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Account setup could not be completed';
      setError(message);
      return {
        kind: 'ACCOUNT_SETUP_INCOMPLETE',
        message,
      };
    } finally {
      setLoading(false);
    }
  };

  const resumeCurrentSession = useCallback(async (): Promise<LoginResult> => {
    let currentUser: User;

    try {
      currentUser = await getCurrentUser();
    } catch (error) {
      console.log('No active Cognito session available to resume', error);
      return { kind: 'NO_ACTIVE_SESSION' };
    }

    try {
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString();

      if (!token) {
        await forceLogout();
        return { kind: 'NO_ACTIVE_SESSION' };
      }

      const hydratedUser = await hydrateAuthenticatedUser(currentUser);
      if (hydratedUser.kind !== 'success') {
        if (hydratedUser.kind === 'AUTH_REQUIRED') {
          await forceLogout();
        }
        const sessionResult: LoginResult = {
          kind: hydratedUser.kind,
          message: hydratedUser.message,
          username: currentUser.username,
          userId: currentUser.userId,
        };
        if (hydratedUser.supportFingerprint) {
          sessionResult.supportFingerprint = hydratedUser.supportFingerprint;
        }
        return sessionResult;
      }

      storeAuthenticatedUserSession(hydratedUser.user, token);
      return {
        kind: 'success',
        access_token: token,
        user: hydratedUser.user,
      };
    } catch (error) {
      console.warn('Failed to resume Cognito session after confirmation', error);
      await forceLogout();
      return { kind: 'NO_ACTIVE_SESSION' };
    }
  }, [forceLogout, hydrateAuthenticatedUser, storeAuthenticatedUserSession]);

  const completeConfirmedSignUp = async (): Promise<LoginResult> => {
    setLoading(true);
    setError(null);

    try {
      const existingSession = await resumeCurrentSession();
      if (existingSession.kind === 'success') {
        return existingSession;
      }

      const response = await autoSignIn();
      if (!response.isSignedIn) {
        const resumedSession = await resumeCurrentSession();
        if (resumedSession.kind === 'success') {
          return resumedSession;
        }

        return {
          kind: 'AUTO_SIGN_IN_FAILED',
          message: resumedSession.message || AUTH_COPY.login.autoSignInFailed,
        };
      }

      const resumedSession = await resumeCurrentSession();
      if (resumedSession.kind === 'success') {
        return resumedSession;
      }

      return {
        kind: resumedSession.kind || 'AUTO_SIGN_IN_FAILED',
        message: resumedSession.message || AUTH_COPY.login.autoSignInFailed,
      };
    } catch (error) {
      console.error('Automatic sign in after signup failed:', error);
      const resumedSession = await resumeCurrentSession();
      if (resumedSession.kind === 'success') {
        return resumedSession;
      }

      await forceLogout();
      return {
        kind: 'AUTO_SIGN_IN_FAILED',
        message: AUTH_COPY.login.autoSignInFailed,
      };
    } finally {
      setLoading(false);
    }
  };

  const completeAutoSignInAfterConfirmation = useCallback(async (): Promise<void> => {
    try {
      const signInResult = await autoSignIn();

      if (signInResult.isSignedIn) {
        const resumedSession = await resumeCurrentSession();
        if (resumedSession.kind !== 'success') {
          console.warn(
            'Amplify autoSignIn completed but session could not be hydrated',
            resumedSession,
          );
        }
      }
    } catch (autoSignInError) {
      console.warn('Amplify autoSignIn was not available after confirmation', autoSignInError);
    }
  }, [resumeCurrentSession]);

  const resolveInvestorSignupUserIdAfterConfirmation = useCallback(
    async ({
      username,
      userId,
      password,
    }: {
      username: string;
      userId?: string;
      password?: string;
    }): Promise<{ userId: string; signedInForCompletion: boolean }> => {
      if (userId) {
        return { userId, signedInForCompletion: false };
      }

      if (!password) {
        throw new Error('Missing Cognito user id for investor signup completion');
      }

      const signInResult = await signIn({
        username,
        password,
      });

      if (!signInResult.isSignedIn) {
        throw new Error('Unable to sign in after investor signup confirmation');
      }

      const currentUser = await getCurrentUser();
      return {
        userId: currentUser.userId,
        signedInForCompletion: true,
      };
    },
    [],
  );

  const handleSignUp = async ({
    email,
    password,
    fullname,
    preferredName,
  }: SignUpParameters): Promise<SignUpResult> => {
    try {
      const screenshotGalleryScenario = getScreenshotGalleryAuthScenario();
      if (screenshotGalleryScenario === 'signup-email-field-failure') {
        return {
          kind: 'SIGNUP_EMAIL_UNAVAILABLE',
          field: 'email' as const,
          message: signupEmailUnavailableMessage,
        };
      }

      if (screenshotGalleryScenario === 'signup-unexpected-next-step') {
        return {
          kind: 'success',
          isSignUpComplete: false,
          userId: 'screenshot-gallery-user',
          nextStep: 'SCREENSHOT_GALLERY_UNEXPECTED_STEP',
        };
      }

      if (isConfirmationScenario(screenshotGalleryScenario)) {
        return {
          kind: 'success',
          isSignUpComplete: false,
          userId: 'screenshot-gallery-user',
          nextStep: 'CONFIRM_SIGN_UP',
        };
      }

      const screenshotGallerySignUpError = screenshotGalleryScenario
        ? buildScreenshotGallerySignUpError(screenshotGalleryScenario)
        : null;
      if (screenshotGallerySignUpError) {
        return {
          kind: getErrorCode(screenshotGallerySignUpError) || 'error',
          message: getFriendlySignUpErrorMessage(screenshotGallerySignUpError),
        };
      }

      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name: fullname,
            nickname: preferredName,
          },
          // optional
          autoSignIn: true, // or SignInOptions e.g { authFlowType: "USER_SRP_AUTH" }
        },
      });

      return {
        kind: 'success',
        isSignUpComplete,
        userId,
        nextStep: nextStep.signUpStep,
      };
    } catch (error: any) {
      console.log('error signing up:', error);

      if (getErrorCode(error) === 'UsernameExistsException') {
        try {
          await resendSignUpCode({ username: email });

          return {
            kind: 'success',
            isSignUpComplete: false,
            nextStep: 'CONFIRM_SIGN_UP',
            message: 'Verification code sent to your email!',
          };
        } catch (resendError: any) {
          console.log('error resending sign up code after duplicate signup', resendError);
          const friendlyResendError = getFriendlyResendSignUpCodeErrorMessage(resendError);

          return {
            kind:
              friendlyResendError.kind === 'INVALID_PARAMETER'
                ? 'SIGNUP_EMAIL_UNAVAILABLE'
                : 'error',
            field:
              friendlyResendError.kind === 'INVALID_PARAMETER' ? ('email' as const) : undefined,
            message:
              friendlyResendError.kind === 'INVALID_PARAMETER'
                ? signupEmailUnavailableMessage
                : friendlyResendError.message,
          };
        }
      }

      return {
        kind: 'error',
        message: getFriendlySignUpErrorMessage(error),
      };
    }
  };

  const signUpConfirmation = async ({
    username,
    confirmationCode,
    inviteToken,
    userId,
    password,
    completeAutoSignIn = true,
  }: ConfirmSignUpParameters): Promise<ConfirmSignUpResult> => {
    const screenshotGalleryScenario = getScreenshotGalleryAuthScenario();
    if (screenshotGalleryScenario === 'confirmation-completion-failure') {
      return buildInvestorSignupCompletionFailureResult(
        inviteToken?.trim() || 'custinvite-screenshot-gallery',
        new Error('PostConfirmation invocation failed for screenshot gallery'),
      );
    }

    if (screenshotGalleryScenario === 'confirmation-unexpected-next-step') {
      return {
        kind: 'success',
        isSignUpComplete: false,
        nextStep: { signUpStep: 'SCREENSHOT_GALLERY_ADDITIONAL_VERIFICATION' },
      };
    }

    const screenshotGalleryConfirmationError = screenshotGalleryScenario
      ? buildScreenshotGalleryConfirmationError(screenshotGalleryScenario)
      : null;
    if (screenshotGalleryConfirmationError) {
      return {
        kind: 'error',
        message: getFriendlySignUpConfirmationErrorMessage(screenshotGalleryConfirmationError),
      };
    }

    const normalizedInviteToken = inviteToken?.trim() || '';
    const hasInviteToken = normalizedInviteToken.length > 0;
    const pendingLocalUserId = hasInviteToken
      ? getPendingLocalInvestorSignupUserId({
          username,
          inviteToken: normalizedInviteToken,
        })
      : null;
    const shouldReturnSupportSignalFailure = Boolean(
      process.env.VITE_LOCAL_INVESTOR_SIGNUP_API_BASE_PATH,
    );
    const shouldCompleteInvestorSignupViaPublicApi =
      hasInviteToken &&
      isInvestorSignupToken(normalizedInviteToken) &&
      Boolean(userId || password || pendingLocalUserId);

    const completeInvestorSignupForResolvedUser = async (): Promise<void> => {
      const resolvedUser = await resolveInvestorSignupUserIdAfterConfirmation({
        username,
        userId: userId || pendingLocalUserId || undefined,
        password,
      });

      try {
        await completeInvestorSignup({
          token: normalizedInviteToken,
          cognito_id: resolvedUser.userId,
          email: username.trim(),
        });
        clearPendingLocalInvestorSignup({
          username,
          inviteToken: normalizedInviteToken,
        });
      } catch (completionError) {
        if (resolvedUser.signedInForCompletion) {
          await forceLogout();
        }

        throw completionError;
      }

      // The password sign-in above only existed to resolve the Cognito user
      // id. Callers that opted out of auto sign-in expect no session
      // afterwards: leaving one behind lets `checkUser` hydrate it on the next
      // load and skip the login page they were sent to.
      if (resolvedUser.signedInForCompletion && !completeAutoSignIn) {
        try {
          await forceLogout();
        } catch (logoutError) {
          // Signup itself succeeded, so surface the cleanup problem without
          // failing the confirmation.
          console.warn('Failed to clear the temporary signup session:', logoutError);
        }
      }
    };

    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username,
        confirmationCode,
        options:
          !hasInviteToken || shouldCompleteInvestorSignupViaPublicApi
            ? undefined
            : {
                clientMetadata: {
                  inviteToken: normalizedInviteToken,
                },
              },
      });

      if (shouldCompleteInvestorSignupViaPublicApi) {
        try {
          await completeInvestorSignupForResolvedUser();
        } catch (completionError) {
          if (shouldReturnSupportSignalFailure) {
            console.log('error completing local investor signup', completionError);
            return buildInvestorSignupCompletionFailureResult(
              normalizedInviteToken,
              completionError,
            );
          }

          return buildInvestorSignupCompletionErrorResult(completionError);
        }
      }

      if (completeAutoSignIn && nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
        await completeAutoSignInAfterConfirmation();
      }

      return {
        kind: 'success',
        isSignUpComplete,
        nextStep,
      };
    } catch (error: any) {
      if (
        shouldCompleteInvestorSignupViaPublicApi &&
        (isAlreadyConfirmedSignUpError(error) || isPostConfirmationInvocationError(error))
      ) {
        try {
          await completeInvestorSignupForResolvedUser();
          return {
            kind: 'success',
            isSignUpComplete: true,
            nextStep: { signUpStep: 'DONE' },
          };
        } catch (completionError) {
          if (shouldReturnSupportSignalFailure) {
            console.log(
              'error completing local investor signup after confirm retry',
              completionError,
            );
          }

          return buildInvestorSignupCompletionFailureResult(normalizedInviteToken, completionError);
        }
      }

      if (
        hasInviteToken &&
        isInvestorSignupToken(normalizedInviteToken) &&
        isAlreadyConfirmedSignUpError(error)
      ) {
        return buildInvestorSignupCompletionFailureResult(
          normalizedInviteToken,
          new Error('Missing Cognito user id for investor signup completion after confirmation'),
        );
      }

      console.log('error confirming sign up', error);
      return {
        kind: 'error',
        message: getFriendlySignUpConfirmationErrorMessage(error),
      };
    }
  };

  const resendSignUpCodeHandler = async (username: string): Promise<ResendSignUpCodeResult> => {
    try {
      const screenshotGalleryScenario = getScreenshotGalleryAuthScenario();
      switch (screenshotGalleryScenario) {
        case 'resend-rate-limited':
          return {
            kind: 'LIMIT_EXCEEDED',
            message: getFriendlyResendSignUpCodeErrorMessage({
              name: 'LimitExceededException',
              message: 'Too many resend attempts for screenshot gallery',
            }).message,
          };

        case 'resend-invalid-parameter':
          return {
            kind: 'INVALID_PARAMETER',
            message: getFriendlyResendSignUpCodeErrorMessage({
              name: 'InvalidParameterException',
              message: 'Invalid resend request for screenshot gallery',
            }).message,
          };

        case 'resend-user-not-found':
          return {
            kind: 'USER_NOT_FOUND',
            message: getFriendlyResendSignUpCodeErrorMessage({
              name: 'UserNotFoundException',
              message: 'Missing signup for screenshot gallery',
            }).message,
          };

        default:
          break;
      }

      await resendSignUpCode({ username });
      return {
        kind: 'success',
        message: 'Confirmation code resent successfully',
      };
    } catch (error: any) {
      console.log('error resending sign up code', error);
      const { kind, message } = getFriendlyResendSignUpCodeErrorMessage(error);

      return {
        kind,
        message,
      };
    }
  };

  const logout = async () => {
    try {
      stopSessionMonitoring();
      await signOut({ global: true });
      clearSentryUser();
      removeItemWithExpiry('access_token');
      removeItemWithExpiry('user');
      setUserState(null);
      store.dispatch(resetAllUserData());
      message.success('logout successfully');
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'logout failed');
      message.error('logout failed');
    }
  };

  const preparePendingSignupVerification = async (email: string): Promise<ForgotPasswordResult> => {
    try {
      await resendSignUpCode({ username: email });
      return {
        kind: 'PENDING_VERIFICATION',
        message: pendingSignupVerificationCodeSentMessage,
        resendCodeWaitSeconds: 60,
      };
    } catch (resendError: any) {
      if (getErrorCode(resendError) === 'LimitExceededException') {
        return {
          kind: 'PENDING_VERIFICATION',
          message: pendingSignupVerificationCodeRecentlySentMessage,
          resendCodeWaitSeconds: 300,
        };
      }

      const friendlyResendError = getFriendlyResendSignUpCodeErrorMessage(resendError);
      return {
        kind: friendlyResendError.kind,
        message: friendlyResendError.message,
      };
    }
  };

  const forgotPassword = async (email: string): Promise<ForgotPasswordResult> => {
    setLoading(true);
    setError(null);
    try {
      const screenshotGalleryScenario = getScreenshotGalleryAuthScenario();
      if (
        screenshotGalleryScenario === 'forgot-password-pending-verification' ||
        screenshotGalleryScenario === 'forgot-password-verification-code-mismatch' ||
        screenshotGalleryScenario === 'forgot-password-verification-expired-code'
      ) {
        return {
          kind: 'PENDING_VERIFICATION',
          message: pendingSignupVerificationCodeSentMessage,
          resendCodeWaitSeconds: 0,
        };
      }

      if (
        screenshotGalleryScenario === 'forgot-password-reset-code-mismatch' ||
        screenshotGalleryScenario === 'forgot-password-reset-expired-code' ||
        screenshotGalleryScenario === 'forgot-password-reset-invalid-password' ||
        screenshotGalleryScenario === 'forgot-password-reset-rate-limited'
      ) {
        return {
          kind: 'success',
          message: 'Password reset code sent successfully',
        };
      }

      if (screenshotGalleryScenario === 'forgot-password-invalid-email') {
        return {
          kind: 'INVALID_PARAMETER',
          message: AUTH_COPY.passwordReset.invalidEmail,
        };
      }

      const screenshotGalleryForgotPasswordError = screenshotGalleryScenario
        ? buildScreenshotGalleryForgotPasswordError(screenshotGalleryScenario)
        : null;
      if (screenshotGalleryForgotPasswordError) {
        throw screenshotGalleryForgotPasswordError;
      }

      await resetPassword({ username: email });
      return {
        kind: 'success',
        message: 'Password reset code sent successfully',
      };
    } catch (error: any) {
      let errorMessage = 'Failed to send reset code';
      let errorKind = 'UNKNOWN_ERROR';

      const errorCode = getErrorCode(error);

      if (errorCode === 'UserNotFoundException') {
        errorKind = 'USER_NOT_FOUND';
        errorMessage = 'User not found';
      } else if (errorCode === 'LimitExceededException') {
        errorKind = 'LIMIT_EXCEEDED';
        errorMessage = 'Too many attempts, please try again later';
      } else if (errorCode === 'InvalidParameterException') {
        const pendingVerificationResult = await preparePendingSignupVerification(email);
        if (pendingVerificationResult.kind === 'PENDING_VERIFICATION') {
          return pendingVerificationResult;
        }

        errorKind = pendingVerificationResult.kind;
        errorMessage = pendingVerificationResult.message || 'Failed to send reset code';
      }

      setError(errorMessage);
      return {
        kind: errorKind,
        message: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  const confirmForgotPassword = async ({
    email,
    confirmationCode,
    newPassword,
  }: ConfirmForgotPasswordParameters): Promise<ConfirmForgotPasswordResult> => {
    setLoading(true);
    setError(null);
    try {
      const screenshotGalleryScenario = getScreenshotGalleryAuthScenario();
      const screenshotGalleryConfirmForgotPasswordError = screenshotGalleryScenario
        ? buildScreenshotGalleryConfirmForgotPasswordError(screenshotGalleryScenario)
        : null;
      if (screenshotGalleryConfirmForgotPasswordError) {
        throw screenshotGalleryConfirmForgotPasswordError;
      }

      await confirmResetPassword({
        username: email,
        confirmationCode,
        newPassword,
      });
      return {
        kind: 'success',
        message: 'Password reset successfully',
      };
    } catch (error: any) {
      let errorMessage = 'Failed to reset password';
      let errorKind = 'UNKNOWN_ERROR';

      const errorCode = getErrorCode(error);

      if (errorCode === 'CodeMismatchException') {
        errorKind = 'INVALID_CODE';
        errorMessage = 'Invalid confirmation code';
      } else if (errorCode === 'ExpiredCodeException') {
        errorKind = 'EXPIRED_CODE';
        errorMessage = 'Confirmation code has expired';
      } else if (errorCode === 'InvalidPasswordException') {
        errorKind = 'INVALID_PASSWORD';
        errorMessage = 'Password does not meet the requirements';
      } else if (errorCode === 'LimitExceededException') {
        errorKind = 'LIMIT_EXCEEDED';
        errorMessage = 'Too many attempts, please try again later';
      }

      setError(errorMessage);
      return {
        kind: errorKind,
        message: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  const clearSession = async () => {
    try {
      stopSessionMonitoring();
      await signOut();
      removeItemWithExpiry('access_token');
      removeItemWithExpiry('user');
      setUserState(null);
      store.dispatch(resetAllUserData());
    } catch (error) {
      console.error('Error clearing session:', error);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    recoverAccountSetup,
    resumeCurrentSession,
    logout,
    handleSignUp,
    signUpConfirmation,
    confirmMfaSignIn,
    resendSignUpCodeHandler,
    completeConfirmedSignUp,
    forgotPassword,
    confirmForgotPassword,
    clearSession,
    isAuthenticated: !!user,
    refreshUserData: checkUser,
    forceLogout,
    detectUserSwitch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
